import { useEffect, useMemo, useReducer, useState } from 'react';
import { PiChairDuotone, PiEyeClosedDuotone, PiEyeDuotone, PiTableDuotone } from 'react-icons/pi';

import { CommanderActionTypesEnum } from '@spazious/dollhouse-commander';
import { useDollhouse, useSelection } from '@spazious/dollhouse-engine';
import {
  FCompositionNode,
  FNodeChild,
  FNodeTypesEnum,
  FurnitureNode,
  getNodeFromObject,
} from '@spazious/dollhouse-fsetup';
import { Translate } from '@spazious/dollhouse-i18n';
import { GraphUpdateAction } from '@spazious/dollhouse-shallow-graph';
import { useGraph, useStaging } from '@spazious/dollhouse-store';
import {
  Flex,
  FlexProps,
  IconButton,
  InputText,
  List,
  ListItemAvatar,
  ListItemContainer,
  ListItemText,
  Select,
  Typography,
} from '@spazious/dollhouse-uikit';

import { DollhouseMessage, MainViewMessage } from '../../core/index.js';
import { useNodeMetadata } from '../../hooks/index.js';
import { useEvents } from '../EventManager/index.js';
import { useFeatures } from '../Features/index.js';

import { InspectorAttributes } from './InspectorAttributes.js';

// TODO unify this util
const formatMetas = (meta: any) =>
  Object.entries(meta ?? {})
    .reduce((acc, [key, value]) => [...acc, `${key}: ${value}`], [] as string[])
    .join(', ');

export interface InspectorFArrangementProps extends FlexProps {
  node?: FCompositionNode;
  editable?: boolean;
}

const filterNodes = (
  nodes: FurnitureNode[],
  search: string,
  visible: 'all' | 'visible' | 'hidden'
) => {
  return nodes.filter((child) => {
    const searchMatch =
      search === '' ||
      child.name.toLowerCase().includes(search.toLowerCase()) ||
      formatMetas(child.meta).toLowerCase().includes(search.toLowerCase());

    if (visible === 'visible') {
      return child.visible && searchMatch;
    }
    if (visible === 'hidden') {
      return !child.visible && searchMatch;
    }
    return searchMatch;
  });
};

export const InspectorFArrangement: React.FC<InspectorFArrangementProps> = ({
  node,
  editable = true,
  ...props
}) => {
  const events = useEvents();
  const { scene } = useDollhouse();
  const { select } = useSelection();
  const { staging, createAction } = useStaging();
  const { selected } = useSelection();
  const { hasFeature } = useFeatures();
  const [hasHiddenNodes, setHasHiddenNodes] = useState(false);
  const [shouldResetInvisibleNodes, setShouldResetInvisibleNodes] = useState(false);
  const { hasInvisiblenodes, resetInvisiblenodes, deletionsCount } = useNodeMetadata();

  const propsReadOnly = !hasFeature('graphs.setup.node.props:editable') || !editable;

  const rootNode =
    node ?? (selected.length ? (getNodeFromObject(selected[0]) as FNodeChild | null) : null);
  const childrenNodes = useMemo(() => {
    const furnitureNodes: FurnitureNode[] = [];

    rootNode?.traverse((node) => {
      if (node.type === FNodeTypesEnum.Furniture) {
        furnitureNodes.push(node);
      }
    });

    return furnitureNodes;
  }, [rootNode]);

  const [{ nodes, search, visible }, dispatch] = useReducer(
    (
      state: {
        search: string;
        visible: 'all' | 'visible' | 'hidden';
        nodes: FurnitureNode[];
      },
      action:
        | {
            type: 'visible';
            payload: 'all' | 'visible' | 'hidden';
          }
        | {
            type: 'search';
            payload: string;
          }
    ) => {
      switch (action.type) {
        case 'visible':
          return {
            ...state,
            nodes: filterNodes(childrenNodes, state.search, action.payload),
            visible: action.payload,
          };
        case 'search':
          return {
            ...state,
            nodes: filterNodes(childrenNodes, action.payload, state.visible),
            search: action.payload,
          };
        default:
          return state;
      }
    },
    {
      search: '',
      visible: 'all',
      nodes: childrenNodes,
    }
  );

  useGraph();

  useEffect(() => {
    const cb = (message: MainViewMessage) => {
      if (message === MainViewMessage.InvisibleNodesResetConfirm) {
        setShouldResetInvisibleNodes(false);
        setHasHiddenNodes(false);
        resetInvisiblenodes(childrenNodes);
      }
    };
    if (shouldResetInvisibleNodes) {
      events.on('mainViewMessage', cb);
      return () => {
        events.off('mainViewMessage', cb);
      };
    }
  }, [childrenNodes, events, resetInvisiblenodes, shouldResetInvisibleNodes]);

  useEffect(() => {
    if (!childrenNodes) {
      setHasHiddenNodes(false);
      return;
    }

    events.emit(
      'dollhouseMessage',
      DollhouseMessage.GetDeletionsCount,
      deletionsCount(childrenNodes)
    );
    setHasHiddenNodes(hasInvisiblenodes(childrenNodes));
  }, [childrenNodes, deletionsCount, events, hasInvisiblenodes]);

  if (selected.length === 0 && !node) {
    return null;
  }

  const handleResetInvisibleNodes = () => {
    setShouldResetInvisibleNodes(true);
  };

  const instance = (node ?? selected[0].userData.node)?.instance;

  if (!instance) {
    return null;
  }

  return (
    <Flex column gap="var(--spacing-padding)" {...props}>
      {instance && (
        <InspectorAttributes
          disabled={propsReadOnly}
          fieldsResetSeating={instance.schema.fieldsResetSeating}
          hasHiddenNodes={hasHiddenNodes}
          order={instance.schema.order}
          schemaFields={instance.toJSON()}
          onValueChange={handleResetInvisibleNodes}
        />
      )}
      {false && (
        <>
          <Flex column alignItems="center" gap="var(--spacing-gap)" justifyContent="space-between">
            <Typography size="large">
              <Translate>Nodes</Translate>
            </Typography>
            <IconButton
              size="small"
              style={{ marginRight: '12px' }}
              onClick={() => {
                staging
                  .prepare()
                  .add(
                    childrenNodes.reduce(
                      (acc, child) =>
                        !child.visible
                          ? [...acc, createAction('update', child.selector!, 'visible', true)]
                          : acc,
                      [] as GraphUpdateAction[]
                    )
                  )
                  .commit(CommanderActionTypesEnum.Remove);
              }}
            >
              <PiEyeDuotone />
            </IconButton>
          </Flex>
          <Flex column gap="var(--spacing-gap)">
            <InputText
              fullWidth
              placeholder="Search"
              value={search}
              onChange={(e) => dispatch({ type: 'search', payload: e.target.value })}
            />

            <Select
              value={visible}
              onChange={(e) => dispatch({ type: 'visible', payload: e.target.value as any })}
            >
              <Select.Option value="all">All nodes</Select.Option>
              <Select.Option value="visible">Visible Only</Select.Option>
              <Select.Option value="hidden">Hidden Only</Select.Option>
            </Select>
          </Flex>
          <Flex column gap="var(--spacing-gap)">
            <List>
              {nodes.length ? (
                nodes.map((child, idx) => (
                  <ListItemContainer key={child.selector} title={child.name}>
                    <ListItemAvatar
                      avatar={
                        /table/.test(child.furnitureType) ? <PiTableDuotone /> : <PiChairDuotone />
                      }
                      style={{ fontSize: '22px' }}
                    />
                    <ListItemText
                      style={{
                        cursor: 'pointer',
                      }}
                      subtitle={formatMetas(child.meta)}
                      title={`${idx + 1} ${child.name}`}
                      onClick={() => {
                        const object = scene.getObjectByProperty('uuid', child.uuid);
                        if (object) {
                          select([object]);
                        }
                      }}
                    />
                    <IconButton
                      size="small"
                      onClick={() => {
                        staging
                          .prepare()
                          .add(createAction('update', child.selector!, 'visible', !child.visible))
                          .commit(CommanderActionTypesEnum.Remove);
                      }}
                    >
                      {child.visible ? <PiEyeDuotone /> : <PiEyeClosedDuotone />}
                    </IconButton>
                  </ListItemContainer>
                ))
              ) : (
                <Flex padding="var(--spacing-padding)">
                  <Typography color="grey">
                    <Translate>No nodes found</Translate>
                  </Typography>
                </Flex>
              )}
            </List>
          </Flex>
        </>
      )}
    </Flex>
  );
};
